import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Category as CategoryIcon } from '@mui/icons-material';
import * as Icons from '@mui/icons-material';
import { Despesa, DespesasFormData, Category } from '../../types';
import expenseService from '../../services/despesasService';
import Swal from 'sweetalert2';
import EscritorioSelect from '../selects/EscritorioSelect';
import { formatarValor, formatarValorOnInput } from '../../utils/formatToView';

interface DespesasMultipleFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedExpense?: Despesa | null;
  categories: Category[];
  title?: string;
}

// Interface para parcela individual
interface Parcela {
  id: number;
  data_vencimento: string;
  valor: number;
  descricao: string;
  valorFormatado: string;
}

/** Remove sufixo " #n de m" ou legado " (n de m)" do final da descrição. */
const REGEX_SUFIXO_PARCELA = /(\s*#\d+\s+de\s+\d+|\s*\(\d+\s+de\s+\d+\))\s*$/;

const removerSufixoParcela = (desc: string): string =>
  desc.replace(REGEX_SUFIXO_PARCELA, '').trimEnd();

/** Garante o sufixo " #parcela de total" no final de cada linha. */
const aplicarSufixosParcelas = (lista: Parcela[], total: number): Parcela[] =>
  lista.map((p, idx) => ({
    ...p,
    descricao: `${removerSufixoParcela(p.descricao)} #${idx + 1} de ${total}`
  }));

// Função para obter data atual no formato YYYY-MM-DD
const getCurrentDate = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Função para adicionar meses a uma data
const adicionarMeses = (dataString: string, meses: number): string => {
  const [year, month, day] = dataString.split('-').map(num => parseInt(num, 10));
  const date = new Date(year, month - 1, day);
  date.setMonth(date.getMonth() + meses);
  
  const newYear = date.getFullYear();
  const newMonth = String(date.getMonth() + 1).padStart(2, '0');
  const newDay = String(date.getDate()).padStart(2, '0');
  
  return `${newYear}-${newMonth}-${newDay}`;
};

const DespesasMultipleForm: React.FC<DespesasMultipleFormProps> = ({
  open,
  onClose,
  onSuccess,
  selectedExpense,
  categories,
  title = 'Nova Despesa Múltipla'
}) => {
  const [formData, setFormData] = useState<DespesasFormData>({
    descricao: '',
    escritorio_id: 0, // Será preenchido com o escritório do usuário logado
    categoria: '',
    tipo: 'fixa',
    valor: 0,
    data_vencimento: getCurrentDate(),
    observacoes: '',
    recorrente: false,
    num_documento: '',
    forma_cobranca: undefined
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [valorFormatado, setValorFormatado] = useState('0,00');

  // Estados para múltiplas parcelas
  const [qtdeParcelas, setQtdeParcelas] = useState<number>(1);
  const [parcelas, setParcelas] = useState<Parcela[]>([]);
  
  // Campo para descrição padrão das parcelas
  const [descricaoPadrao, setDescricaoPadrao] = useState<string>('');
  
  // Campo para valor padrão das parcelas
  const [valorPadrao, setValorPadrao] = useState<string>('0,00');

  // Função auxiliar para criar Date a partir de string YYYY-MM-DD sem problemas de fuso horário
  const criarDataSegura = (dataString: string): Date | null => {
    if (!dataString || dataString === '') return null;
    
    try {
      // Separar os componentes da data
      const [year, month, day] = dataString.split('-').map(num => parseInt(num, 10));
      
      // Validar se os valores são válidos
      if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
      if (year < 1900 || year > 2100) return null;
      if (month < 1 || month > 12) return null;
      if (day < 1 || day > 31) return null;
      
      // Criar Date usando construtor com componentes (evita problemas de fuso horário)
      const date = new Date(year, month - 1, day); // month é 0-based
      
      // Verificar se a data criada é válida
      if (isNaN(date.getTime())) return null;
      
      // Verificar se os componentes correspondem (previne datas como 31/02)
      if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null;
      }
      
      return date;
    } catch (error) {
      console.error('Erro ao criar data segura:', error);
      return null;
    }
  };

  // Função auxiliar para formatar Date para string YYYY-MM-DD
  const formatarDataSegura = (date: Date): string => {
    if (!date || isNaN(date.getTime())) return '';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };

  
  // Função para converter string formatada para número
  const parsearMoeda = (valorFormatado: string): number => {
    // Remove todos os caracteres que não são números, vírgula ou ponto
    const numeroLimpo = valorFormatado.replace(/[^\d,]/g, '');
    // Substitui vírgula por ponto para conversão
    const numeroComPonto = numeroLimpo.replace(',', '.');
    const numero = parseFloat(numeroComPonto) || 0;
    return numero;
  };

  /** Mesmo padrão do Valor Padrão: `formatarValorOnInput` sem R$ (para células com adorno R$). */
  const valorNumericoParaCampoMoeda = (valor: number) =>
    formatarValorOnInput(String(Math.round((Number.isFinite(valor) ? valor : 0) * 100)));

  // Função para gerar parcelas
  const gerarParcelas = (quantidade: number, dataInicial: string, valorBase: number, descricaoBase: string) => {
    const novasParcelas: Parcela[] = [];
    
    for (let i = 0; i < quantidade; i++) {
      let dataVencimento = adicionarMeses(dataInicial, i);
      
      const mesReferencia = `${String(new Date(dataVencimento).getMonth() + 1).padStart(2, '0')}-${new Date(dataVencimento).getFullYear()}`;
      
      // Usar descricaoPadrao se disponível, senão usar descricaoBase
      const descricaoBaseLinha = descricaoPadrao ? `${descricaoPadrao}` : `${descricaoBase} - ${mesReferencia}`;
      const descricaoFinal = `${descricaoBaseLinha} #${i + 1} de ${quantidade}`;
      
      // Usar valorPadrao se disponível, senão usar valorBase
      const valorFinal = valorPadrao && valorPadrao !== '0,00' ? parsearMoeda(valorPadrao) : valorBase;
      
      novasParcelas.push({
        id: i + 1,
        data_vencimento: dataVencimento,
        valor: valorFinal,
        descricao: descricaoFinal,
        valorFormatado: valorNumericoParaCampoMoeda(valorFinal)
      });
    }
    
    return novasParcelas;
  };

  // Preencher formulário quando editando
  useEffect(() => {
    if (selectedExpense && open) {
      // Editando despesa existente
      const valorNum = Number(selectedExpense.valor) || 0;

      // Validar e formatar data de vencimento
      let dataVencimento = '';
      if (selectedExpense.data_vencimento) {
        try {
          // Primeiro tentar como string já formatada (YYYY-MM-DD)
          if (typeof selectedExpense.data_vencimento === 'string' && selectedExpense.data_vencimento.match(/^\d{4}-\d{2}-\d{2}$/)) {
            dataVencimento = selectedExpense.data_vencimento;
          } else {
            // Se não, tentar converter de ISO ou outro formato
            const date = new Date(selectedExpense.data_vencimento);
            if (!isNaN(date.getTime())) {
              dataVencimento = formatarDataSegura(date);
            }
          }
        } catch (error) {
          console.error('Erro ao processar data de vencimento:', error);
          dataVencimento = '';
        }
      }

      setFormData({
        descricao: selectedExpense.descricao || '',
        escritorio_id: selectedExpense.escritorio_id || 0,
        categoria: selectedExpense.categoria || '',
        tipo: selectedExpense.tipo || 'fixa',
        valor: valorNum,
        data_vencimento: dataVencimento,
        observacoes: selectedExpense.observacoes || '',
        recorrente: selectedExpense.recorrente || false,
        num_documento: selectedExpense.num_documento || '',
        forma_cobranca: selectedExpense.forma_cobranca || undefined
      });

      // Formatar valores para exibição
      setValorFormatado(formatarValor(valorNum));

      // Para edição, sempre usar quantidade 1
      setQtdeParcelas(1);
      setParcelas([]);
    } else if (open) {
      // Nova despesa
      setFormData({
        descricao: '',
        escritorio_id: 0,
        categoria: '',
        tipo: 'fixa',
        valor: 0,
        data_vencimento: getCurrentDate(),
        observacoes: '',
        recorrente: false,
        num_documento: '',
        forma_cobranca: undefined
      });

      // Resetar valores formatados
      setValorFormatado('0,00');

      // Resetar quantidade de parcelas
      setQtdeParcelas(1);
      setParcelas([]);
    }
  }, [selectedExpense, open]);

  // Efeito para inicializar parcelas quando o formulário abrir
  useEffect(() => {
    if (open && qtdeParcelas >= 1 && parcelas.length === 0) {
      // Gerar parcela inicial apenas se não houver parcelas
      const dataInicial = formData.data_vencimento || getCurrentDate();
      const valorBase = formData.valor || 0;
      const descricaoBase = formData.descricao || 'Despesa'; // Descrição padrão se estiver vazia

      const parcelaInicial = gerarParcelas(
        qtdeParcelas,
        dataInicial,
        valorBase,
        descricaoBase
      );
      setParcelas(parcelaInicial);
    }
  }, [open, qtdeParcelas, parcelas.length, descricaoPadrao]);

  const handleInputChange = (field: keyof DespesasFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handlers para campos de moeda
  const handleValorChange = (valor: string) => {
    const valorFormatado = formatarValorOnInput(valor);
    const valorNumerico = parsearMoeda(valorFormatado);
    
    setValorFormatado(valorFormatado);
    handleInputChange('valor', valorNumerico);
  };

  /** Atualiza a data do 1º vencimento e regenera todas as parcelas (mensal) a partir dela. */
  const handlePrimeiroVencimentoChange = (value: string) => {
    const dataInicial = value && value.trim() !== '' ? value : getCurrentDate();
    setFormData(prev => ({
      ...prev,
      data_vencimento: dataInicial
    }));
    const valorBase = formData.valor || 0;
    const descricaoBase = formData.descricao || 'Despesa';
    setParcelas(gerarParcelas(qtdeParcelas, dataInicial, valorBase, descricaoBase));
  };

  // Handler para mudança na quantidade de parcelas
  const handleQtdeParcelasChange = (novaQuantidade: number) => {
    const quantidadeAtual = parcelas.length;
    setQtdeParcelas(novaQuantidade);
    
    if (novaQuantidade > quantidadeAtual) {
      // Adicionar novas parcelas preservando as existentes
      const novasParcelas = [...parcelas];
      const dataInicial = formData.data_vencimento || getCurrentDate();
      const valorBase = formData.valor || 0;
      const descricaoBase = formData.descricao || 'Despesa'; // Descrição padrão se estiver vazia

      for (let i = quantidadeAtual; i < novaQuantidade; i++) {
        let dataVencimento = adicionarMeses(dataInicial, i);
        
        const mesReferencia = `${String(new Date(dataVencimento).getMonth() + 1).padStart(2, '0')}-${new Date(dataVencimento).getFullYear()}`;
        
        // Usar descricaoPadrao se disponível, senão usar descricaoBase (sufixo recalculado depois)
        const descricaoLinha = descricaoPadrao ? `${descricaoPadrao}` : `${descricaoBase} - ${mesReferencia}`;
        
        // Usar valorPadrao se disponível, senão usar valorBase
        const valorFinal = valorPadrao && valorPadrao !== '0,00' ? parsearMoeda(valorPadrao) : valorBase;
        
        novasParcelas.push({
          id: i + 1,
          data_vencimento: dataVencimento,
          valor: valorFinal,
          descricao: descricaoLinha,
          valorFormatado: valorNumericoParaCampoMoeda(valorFinal)
        });
      }
      
      setParcelas(aplicarSufixosParcelas(novasParcelas, novaQuantidade));
    } else if (novaQuantidade < quantidadeAtual) {
      // Remover parcelas extras preservando as primeiras
      const novasParcelas = aplicarSufixosParcelas(
        parcelas.slice(0, novaQuantidade),
        novaQuantidade
      );
      setParcelas(novasParcelas);
    }
    // Se novaQuantidade === quantidadeAtual, não faz nada (preserva as existentes)
  };

  // Handlers para parcelas
  const handleParcelaChange = (index: number, field: keyof Parcela, value: any) => {
    const novasParcelas = [...parcelas];
    
    if (field === 'valorFormatado') {
      const valorFormatado = formatarValorOnInput(value);
      const valorNumerico = parsearMoeda(valorFormatado);
      
      novasParcelas[index] = {
        ...novasParcelas[index],
        [field]: valorFormatado,
        valor: valorNumerico
      };
    } else {
      novasParcelas[index] = {
        ...novasParcelas[index],
        [field]: value
      };
    }
    
    setParcelas(novasParcelas);
  };

  // Handler para mudança na descrição padrão
  const handleDescricaoPadraoChange = (novaDescricao: string) => {
    setDescricaoPadrao(novaDescricao);
    
    // Atualizar todas as parcelas existentes com a nova descrição padrão
    if (parcelas.length > 0) {
      const total = parcelas.length;
      const parcelasAtualizadas = parcelas.map((parcela, index) => {
        const mesReferencia = `${String(new Date(parcela.data_vencimento).getMonth() + 1).padStart(2, '0')}-${new Date(parcela.data_vencimento).getFullYear()}`;
        const descricaoBaseLinha = novaDescricao ? `${novaDescricao}` : `Despesa - ${mesReferencia}`;
        const descricaoFinal = `${descricaoBaseLinha} #${index + 1} de ${total}`;
        
        return {
          ...parcela,
          descricao: descricaoFinal
        };
      });
      
      setParcelas(parcelasAtualizadas);
    }
  };

  // Handler para mudança no valor padrão
  const handleValorPadraoChange = (valor: string) => {
    const valorFormatado = formatarValorOnInput(valor);
    setValorPadrao(valorFormatado);
    
    // Atualizar todas as parcelas existentes com o novo valor padrão
    if (parcelas.length > 0) {
      const valorNumerico = parsearMoeda(valorFormatado);
      const parcelasAtualizadas = parcelas.map((parcela) => ({
        ...parcela,
        valor: valorNumerico,
        valorFormatado: valorNumericoParaCampoMoeda(valorNumerico)
      }));
      
      setParcelas(parcelasAtualizadas);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setErrors([]);

    try {
      // Validar e submeter despesas
      const validationErrors: string[] = [];
      
      // Validar dados básicos
      if (!formData.escritorio_id) {
        validationErrors.push('Escritório é obrigatório');
      }
      if (!formData.categoria) {
        validationErrors.push('Categoria é obrigatória');
      }
      if (!formData.tipo) {
        validationErrors.push('Tipo é obrigatório');
      }
      if (!formData.forma_cobranca) {
        validationErrors.push('Forma de cobrança é obrigatória');
      }

      // Validar parcelas
      parcelas.forEach((parcela, index) => {
        if (!parcela.data_vencimento) {
          validationErrors.push(`Data de vencimento da parcela ${index + 1} é obrigatória`);
        }
        if (parcela.valor <= 0) {
          validationErrors.push(`Valor da parcela ${index + 1} deve ser maior que zero`);
        }
        if (!parcela.descricao) {
          validationErrors.push(`Descrição da parcela ${index + 1} é obrigatória`);
        }
      });

      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        setLoading(false);
        return;
      }

      // Criar múltiplas despesas
      const despesasData: DespesasFormData[] = parcelas.map((parcela, index) => ({
        descricao: parcela.descricao,
        escritorio_id: formData.escritorio_id,
        categoria: formData.categoria,
        tipo: formData.tipo,
        valor: parcela.valor,
        data_vencimento: parcela.data_vencimento,
        observacoes: formData.observacoes,
        recorrente: formData.recorrente,
        num_documento: formData.num_documento,
        forma_cobranca: formData.forma_cobranca,
        qtde_parcela: parcelas.length, // Total de parcelas
        num_parcela: index + 1 // Número sequencial da parcela (1, 2, 3, 4...)
      }));

      // Mostrar progresso de criação
      await handleBulkCreateWithProgress(despesasData);
    } catch (error: any) {
      console.error('Erro ao salvar despesa(s):', error);
      setErrors([error.message || 'Erro ao salvar despesa(s)']);
    } finally {
      setLoading(false);
    }
  };

  // Calcular total das parcelas
  const calcularTotalParcelas = () => {
    return parcelas.reduce((total, parcela) => {
      return total + parcela.valor;
    }, 0);
  };

  // Função para criar despesas com progresso
  const handleBulkCreateWithProgress = async (despesasParaCriar: DespesasFormData[]) => {
    try {
      // Fechar o formulário primeiro
      onClose();
      
      // Mostrar SweetAlert com progresso
      const { value: confirmed } = await Swal.fire({
        title: 'Criando Despesas',
        html: `
          <div id="progress-container">
            <div style="text-align: center; margin-bottom: 20px;">
              <div class="swal2-progress-bar" style="width: 100%; height: 4px; background: #e0e0e0; border-radius: 2px; overflow: hidden;">
                <div id="progress-bar" style="width: 0%; height: 100%; background: #4caf50; transition: width 0.3s;"></div>
              </div>
              <div id="progress-text" style="margin-top: 10px; font-size: 14px; color: #666;">
                Preparando criação...
              </div>
            </div>
            <div id="despesas-progress" style="max-height: 300px; overflow-y: auto; text-align: left;">
              ${despesasParaCriar.map((despesa, index) => `
                <div id="despesa-${index}" style="padding: 8px; margin: 4px 0; border-radius: 4px; background: #f5f5f5; display: flex; justify-content: space-between; align-items: center;">
                  <span style="font-weight: 500;">Despesa ${index + 1}</span>
                  <span id="status-${index}" style="color: #666;">Aguardando...</span>
                </div>
              `).join('')}
            </div>
          </div>
        `,
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: async () => {
          const progressBar = document.getElementById('progress-bar') as HTMLElement;
          const progressText = document.getElementById('progress-text') as HTMLElement;
          
          let sucessos = 0;
          let erros = 0;
          const despesasCriadas: Despesa[] = [];
          
          for (let i = 0; i < despesasParaCriar.length; i++) {
            const despesa = despesasParaCriar[i];
            const statusElement = document.getElementById(`status-${i}`) as HTMLElement;
            const despesaElement = document.getElementById(`despesa-${i}`) as HTMLElement;
            
            try {
              // Atualizar texto de progresso
              progressText.textContent = `Criando despesa ${i + 1}...`;
              
              // Atualizar barra de progresso
              const progress = ((i + 1) / despesasParaCriar.length) * 100;
              progressBar.style.width = `${progress}%`;
              
              // Atualizar status da despesa
              statusElement.textContent = 'Criando...';
              statusElement.style.color = '#ff9800';
              despesaElement.style.background = '#fff3e0';
              
              // Aguardar um pouco para mostrar o progresso
              await new Promise(resolve => setTimeout(resolve, 500));
              
              // Fazer a chamada da API
              const response = await expenseService.cadastrarDespesa(despesa);
              
              if (response && response.success && response.data) {
                statusElement.textContent = 'Criada ✅';
                statusElement.style.color = '#4caf50';
                despesaElement.style.background = '#e8f5e8';
                sucessos++;
                despesasCriadas.push(response.data);
              } else {
                statusElement.textContent = 'Erro ❌';
                statusElement.style.color = '#f44336';
                despesaElement.style.background = '#ffebee';
                erros++;
              }
              
            } catch (error: any) {
              console.error(`Erro ao criar despesa ${i + 1}:`, error);
              statusElement.textContent = 'Erro ❌';
              statusElement.style.color = '#f44336';
              despesaElement.style.background = '#ffebee';
              erros++;
            }
            
            // Aguardar um pouco entre as criações
            await new Promise(resolve => setTimeout(resolve, 300));
          }
          
          // Atualizar texto final
          progressText.textContent = `Concluído! ${sucessos} criadas, ${erros} erros.`;
          progressBar.style.width = '100%';
          
          // Aguardar um pouco antes de fechar
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Fechar o SweetAlert
          Swal.close();
          
          // Mostrar resultado final
          if (erros === 0) {
            Swal.fire({
              icon: 'success',
              title: 'Criação Concluída!',
              text: `Todas as ${sucessos} despesas foram criadas com sucesso.`,
              confirmButtonText: 'OK'
            });
          } else {
            Swal.fire({
              icon: 'warning',
              title: 'Criação Concluída',
              text: `${sucessos} despesas criadas com sucesso. ${erros} despesas com erro.`,
              confirmButtonText: 'OK'
            });
          }
          
          // Recarregar dados se necessário
          if (onSuccess) {
            onSuccess();
          }
          
        }
      });
      
    } catch (error: any) {
      console.error('Erro durante criação em massa:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro na Criação',
        text: 'Ocorreu um erro durante a criação das despesas.',
        confirmButtonText: 'OK'
      });
    }
  };

  const getCategoryByCode = (codigo: string): Category | undefined => {
    return categories.find(cat => cat.codigo === codigo);
  };

  const renderCategoryIcon = (categoria: string, size: 'small' | 'medium' = 'small') => {
    const category = getCategoryByCode(categoria);
    if (!category || !category.icone) return <CategoryIcon />;
    
    const IconComponent = (Icons as any)[category.icone];
    return IconComponent ? <IconComponent /> : <CategoryIcon />;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ p: 1.5 }}>
        <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 1.5 }}>
        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
            {errors.map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          </Alert>
        )}

        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
          {/* Escritório */}
          <Grid item xs={12} md={12}>
            <EscritorioSelect
              value={formData.escritorio_id || null}
              onChange={(value) => handleInputChange('escritorio_id', value as number)}
              label="Escritório *"
              size="small"
              required={true}
              allowClear={false}
              multiple={false}
              onlyActive={true}
              error={!!errors.some(e => e.includes('escritório'))}
              helperText={errors.find(e => e.includes('escritório'))}
            />
          </Grid>
          
          {/* Categoria */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Categoria *</InputLabel>
              <Select
                value={formData.categoria}
                onChange={(e) => handleInputChange('categoria', e.target.value)}
                label="Categoria *"
                size="small"
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.codigo}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {renderCategoryIcon(category.codigo, 'small')}
                      {category.nome}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Tipo */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo *</InputLabel>
              <Select
                value={formData.tipo}
                onChange={(e) => handleInputChange('tipo', e.target.value)}
                label="Tipo *"
                size="small"
              >
                <MenuItem value="fixa">Fixa</MenuItem>
                <MenuItem value="variavel">Variável</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Forma de Cobrança */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Forma de Cobrança *</InputLabel>
              <Select
                value={formData.forma_cobranca || ''}
                onChange={(e) => handleInputChange('forma_cobranca', e.target.value)}
                label="Forma de Cobrança *"
                size="small"
              >
                <MenuItem value="">
                  <em>Selecione...</em>
                </MenuItem>
                <MenuItem value="boleto">Boleto</MenuItem>
                <MenuItem value="cartao_credito">Cartão de Crédito</MenuItem>
                <MenuItem value="cartao_debito">Cartão de Débito</MenuItem>
                <MenuItem value="cheque">Cheque</MenuItem>
                <MenuItem value="debito_automatico">Débito Automático</MenuItem>
                <MenuItem value="deposito">Depósito</MenuItem>
                <MenuItem value="dinheiro">Dinheiro</MenuItem>
                <MenuItem value="pix">PIX</MenuItem>
                <MenuItem value="promissoria">Promissória</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Número do Documento */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Número do Documento"
              fullWidth
              size="small"
              value={formData.num_documento}
              onChange={(e) => handleInputChange('num_documento', e.target.value)}
              placeholder="Ex: 001/2025, NF-123456"
            />
          </Grid>

          {/* Primeiro vencimento — base para gerar datas das parcelas (1ª = data informada; demais +1 mês cada) */}
          <Grid item xs={12} md={5}>
            <TextField
              label="Primeiro vencimento *"
              type="date"
              fullWidth
              size="small"
              value={formData.data_vencimento || ''}
              onChange={(e) => handlePrimeiroVencimentoChange(e.target.value)}
              InputLabelProps={{ shrink: true }}              
            />
          </Grid>

          {/* Quantidade de Parcelas */}
          <Grid item xs={12} md={3}>
            <TextField
              label="Qtde Parcelas"
              type="number"
              fullWidth
              size="small"
              value={qtdeParcelas}
              onChange={(e) => handleQtdeParcelasChange(Number(e.target.value) || 1)}
              inputProps={{ min: 1, max: 56 }}
            />
          </Grid>

          {/* Valor Padrão */}
          <Grid item xs={12} md={4}>
            <TextField
              label="Valor Padrão"
              fullWidth
              size="small"
              value={valorPadrao}
              onChange={(e) => handleValorPadraoChange(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              }}
            />
          </Grid>
          

          {/* Descrição Padrão */}
          <Grid item xs={12} md={12}>
            <TextField
              label="Descrição Padrão"
              fullWidth
              size="small"
              value={descricaoPadrao}
              onChange={(e) => handleDescricaoPadraoChange(e.target.value)}
              placeholder="Ex: Aluguel, Internet, Energia"
            />
          </Grid>

          {/* Observações */}
          <Grid item xs={12}>
            <TextField
              label="Observações"
              fullWidth
              size="small"
              multiline
              rows={2}
              value={formData.observacoes}
              onChange={(e) => handleInputChange('observacoes', e.target.value)}
            />
          </Grid>

          {/* Recorrente */}
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.recorrente}
                  onChange={(e) => handleInputChange('recorrente', e.target.checked)}
                />
              }
              label="Despesa Recorrente"
            />
          </Grid>

          {/* Tabela de Parcelas */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem' }}>
              Parcelas ({qtdeParcelas})
            </Typography>
            <TableContainer component={Paper} sx={{ maxHeight: 400 }} elevation={1}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.8rem' }}>Num.</TableCell>
                    <TableCell sx={{ width: '100px', py: 0.5, px: 1, fontSize: '0.8rem' }}>Data Vencimento</TableCell>
                    <TableCell sx={{ width: '180px', py: 0.5, px: 1, fontSize: '0.8rem' }}>Valor</TableCell>
                    <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.8rem' }}>Descrição</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parcelas.map((parcela, index) => (
                    <TableRow key={parcela.id}>
                      <TableCell sx={{ width: '40px', py: 0.2, px: 1, fontSize: '0.8rem' }}>{index + 1}</TableCell>
                      <TableCell sx={{ width: '100px', py: 0.2, px: 1 }}>
                        <TextField
                          type="date"
                          size="small"
                          value={parcela.data_vencimento}
                          onChange={(e) => handleParcelaChange(index, 'data_vencimento', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </TableCell>
                      <TableCell sx={{ width: '180px', py: 0.2, px: 1 }}>
                        <TextField
                          size="small"
                          value={parcela.valorFormatado}
                          onChange={(e) => handleParcelaChange(index, 'valorFormatado', e.target.value)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                          }}                          
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.2, px: 1 }}>
                        <TextField
                          size="small"
                          fullWidth
                          value={parcela.descricao}
                          onChange={(e) => handleParcelaChange(index, 'descricao', e.target.value)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Total das parcelas */}
            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="h6" color="primary" sx={{ fontSize: '1.1rem' }}>
                Total das Parcelas: {formatarValor(calcularTotalParcelas())}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={onClose} disabled={loading} size="small" sx={{ fontSize: '0.8rem' }}>
          Cancelar
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
          size="small"
          sx={{ fontSize: '0.8rem' }}
        >
          {loading ? 'Salvando...' : `Gerar ${qtdeParcelas} Parcela${qtdeParcelas > 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DespesasMultipleForm;
